import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";
import { RED, RED_LIGHT, GREEN, GREEN_LIGHT, MUTED } from "assets/theme/brandColors";

const TIER_STYLE = {
  Tried: { fg: "#7a6a2f", bg: "#fdf6e0" },
  Practiced: { fg: RED, bg: RED_LIGHT },
  Comfortable: { fg: GREEN, bg: GREEN_LIGHT },
  Instinct: { fg: "#fff", bg: GREEN },
};

/**
 * "Your Kitchen" — the three techniques that moved most recently, plus one
 * forward pointer.
 *
 * Sits beside the goals table rather than inside it: a goal is something the
 * user signed up for, a tier is a read on what they can now do, and conflating
 * the two makes the second one look like another counter.
 *
 * Deferred techniques cannot appear here — /api/techniques/me/card filters them
 * out server-side, since they carry no blurb to show.
 */
function TechniqueCard() {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiClient
      .get(ENDPOINTS.techniques.card)
      .then((res) => setCard(res.data))
      .catch(() => setFailed(true));
  }, []);

  if (failed) return null;

  const recent = card?.recent || [];
  const nextUp = card?.next_up;

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDTypography variant="h6" fontWeight="medium" mb={2}>
          Your Kitchen
        </MDTypography>

        {recent.length === 0 ? (
          <MDTypography variant="body2" color="secondary" textAlign="center" py={2}>
            Cook a recipe and your first technique appears here 🔪
          </MDTypography>
        ) : (
          recent.map((technique, index) => {
            const style = TIER_STYLE[technique.tier] || TIER_STYLE.Tried;
            return (
              <MDBox
                key={technique.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                py={1.25}
                sx={{
                  borderBottom: index < recent.length - 1 ? "1px solid #f5f5f5" : "none",
                }}
              >
                <MDTypography
                  variant="button"
                  color="text"
                  sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                >
                  {technique.display_name}
                </MDTypography>
                <MDBox
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    textTransform: "uppercase",
                    color: style.fg,
                    bgcolor: style.bg,
                    borderRadius: "999px",
                    px: "9px",
                    py: "3px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {technique.tier}
                </MDBox>
              </MDBox>
            );
          })
        )}

        {nextUp && (
          <MDBox
            mt={2}
            pt={2}
            sx={{ borderTop: "1px solid #f5f5f5", cursor: "pointer" }}
            onClick={() => navigate("/your-kitchen")}
          >
            <MDTypography
              variant="caption"
              sx={{ color: MUTED, textTransform: "uppercase", letterSpacing: "1px", fontSize: 10 }}
            >
              Next
            </MDTypography>
            <MDTypography variant="body2" sx={{ fontSize: "0.85rem", mt: 0.5, lineHeight: 1.45 }}>
              {nextUp.tier === "unseen"
                ? `You haven't tried ${nextUp.display_name} yet.`
                : `${nextUp.reps_to_next} more and ${nextUp.display_name} levels up.`}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

export default TechniqueCard;
